<template>
    <div>
        <g-row class="g-mt-8 g-mb-7">
            <g-column :span="24" class="g-mb-5">
                <g-typography tag="h2" font-weight="semibold" size="h1" text-align="center">
                    {{ title }}
                </g-typography>
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-7">
            <g-column>
                <g-form class="g-pb-0 g-mb-5">
                    <g-form-item label="Name on Card" class="g-mb-3">
                        <g-input v-model="cardName" type="text" required="true" />
                    </g-form-item>

                    <g-form-item label="Card Number" class="g-mb-2">
                        <g-input v-if="editMode()" :value="editCardNumberMasked" type="text" disabled />
                        <div v-else id="card-number"></div>
                    </g-form-item>

                    <g-row type="flex" :gutter="16" class="xs-break">
                        <g-column :span="8">
                            <g-form-item label="Security Code" class="g-mb-0" style="padding-bottom: 0">
                                <g-input v-if="editMode()" :value="editSecurityCodeMasked" type="text" disabled />
                                <div v-else id="card-security-code"></div>
                            </g-form-item>
                        </g-column>
                        <g-column :span="8">
                            <g-form-item label="Expiration" class="g-mb-0" style="padding-bottom: 0">
                                <g-input
                                    v-if="editMode()"
                                    v-model="editExpiration"
                                    v-mask="'##/##'"
                                    placeholder="MM/YY"
                                    type="text"
                                    required="true"
                                />
                                <div v-else id="card-expiration"></div>
                            </g-form-item>
                        </g-column>
                        <g-column :span="8">
                            <g-form-item label="Zip/Postal Code" class="g-mb-0" style="padding-bottom: 0">
                                <g-input
                                    v-model="postalCode"
                                    type="text"
                                    required="true"
                                    :max-length="FORM_POSTAL_CODE_MAX_LENGTH"
                                />
                            </g-form-item>
                        </g-column>
                    </g-row>

                    <g-row type="flex" :gutter="16" class="g-mt-4 xs-break">
                        <g-column :span="12">
                            <g-typography tag="h4" font-weight="label" class="g-mb-3">
                                Country
                                <g-tooltip
                                    class="tooltip"
                                    placement="top"
                                    title="List of countries we currently support."
                                >
                                    <g-icon size="small" class="g-text-grey-5 g-mr-3" name="question-circle" />
                                </g-tooltip>
                            </g-typography>

                            <g-select
                                v-model="selectedCountry"
                                class="country-select"
                                show-search
                                option-filter-prop="children"
                                :disabled="editMode()"
                                @change="handleUpdateCurrencies"
                            >
                                <g-select-option
                                    v-for="(country, code) in PAYOUTS_COUNTRY_CURRENCIES"
                                    :key="code"
                                    :value="code"
                                >
                                    {{ country.name }}
                                </g-select-option>
                            </g-select>
                        </g-column>

                        <g-column :span="12">
                            <g-typography tag="h4" font-weight="label" class="g-mb-3">
                                Account Currency
                                <g-tooltip
                                    class="tooltip"
                                    placement="top"
                                    title="List of currencies we currently support."
                                >
                                    <g-icon size="small" class="g-text-grey-5 g-mr-3" name="question-circle" />
                                </g-tooltip>
                            </g-typography>

                            <g-select v-model="selectedCurrency" placeholder="Select" :disabled="editMode()">
                                <g-select-option v-for="(currency, i) in currencies" :key="i" :value="currency.code">
                                    {{ currency.name }}
                                </g-select-option>
                            </g-select>
                        </g-column>
                    </g-row>

                    <g-row>
                        <g-column>
                            <g-divider margin="large" />
                        </g-column>
                    </g-row>

                    <g-row type="flex" justify="space-between" class="g-mb-5">
                        <g-column>
                            <g-typography tag="h4" font-weight="label">
                                {{ DEFAULT_PAYOUT_METHOD_LABEL_STRING }}
                            </g-typography>
                        </g-column>

                        <g-column>
                            <g-switch v-model="isDefault" :disabled="firstPayoutMethod && !editMode()">
                                <g-icon slot="checkedChildren" name="check" size="xsmall" />
                                <g-icon slot="unCheckedChildren" name="times" size="xsmall" />
                            </g-switch>
                        </g-column>
                    </g-row>

                    <g-row>
                        <g-column>
                            <g-typography tag="p" class="g-mb-3">
                                {{ DEFAULT_PAYOUT_METHOD_LINE_1_STRING }}
                            </g-typography>

                            <g-typography tag="p">
                                {{ DEFAULT_PAYOUT_METHOD_LINE_2_STRING }}
                            </g-typography>
                        </g-column>
                    </g-row>
                </g-form>
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-4">
            <g-column>
                <g-button
                    shape="round"
                    theme="live"
                    type="primary"
                    size="large"
                    :loading="isSubmitting"
                    :disabled="!isReady() || isSubmitting"
                    @click="handleSubmit"
                >
                    {{ primaryButtonText }}
                </g-button>
            </g-column>
        </g-row>

        <g-row type="flex" align="middle" justify="space-around" class="g-mb-4">
            <g-column>
                <g-button theme="live" :border="false" :shadow="false" @click="handleSecondaryAction">
                    {{ secondaryButtonText }}
                </g-button>
            </g-column>
        </g-row>
    </div>
</template>

<script>
    import '@stripe/stripe-js';
    import { onMounted, ref, toRefs, watch, computed } from 'vue-demi';
    import debounce from 'lodash/debounce';
    import { FORM_POSTAL_CODE_MAX_LENGTH, FORM_POSTAL_CODE_REGEXP } from '../../../../../app-v2/constants/forms';
    import {
        DEFAULT_PAYOUT_METHOD_LABEL_STRING,
        DEFAULT_PAYOUT_METHOD_LINE_1_STRING,
        DEFAULT_PAYOUT_METHOD_LINE_2_STRING,
        PAYOUT_METHOD_MODE_EDIT
    } from '../../../../../app-v2/constants/payouts';
    import { PAYOUTS_COUNTRY_CURRENCIES } from '../../../../../app-v2/constants/payouts-currencies';
    import { EVENT_NAME_PAYOUTS_DEBIT_CARD_SUBMITTED } from '../../../../../app-v2/constants/analytics-events';
    import useToggle from '../../../../composables/use-toggle';
    import usePayoutMethods from '../../../../composables/use-payout-methods';
    import useMessage from '../../../../composables/use-message';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';
    import useContactStore from '../../../../../stores/contact';

    const STRIPE_STYLING = {
        classes: {
            base: 'ant-input'
        },
        style: {
            base: {
                color: 'rgba(0, 0, 0, 0.65)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '14px',
                lineHeight: 1.5
            }
        }
    };

    export default {
        name: 'DebitCard',

        props: {
            payoutAccount: {
                type: Object,
                default: null
            },
            payoutMethod: {
                type: Object,
                default: null
            },
            mode: {
                type: String,
                default: ''
            }
        },

        setup(props, context) {
            const events = useEventTracking();
            const contact = useContactStore().data;
            const { message } = useMessage();
            const cardName = ref('');
            const postalCode = ref('');
            const editExpiration = ref('');
            const editCardNumberMasked = ref('');
            const editSecurityCodeMasked = ref('***');
            const cardNumberReady = ref(false);
            const securityCodeReady = ref(false);
            const cardExpirationReady = ref(false);
            const isDefault = ref(false);
            const firstPayoutMethod = ref(false);

            firstPayoutMethod.value = !props.payoutAccount?.payout_method_id;
            if (firstPayoutMethod.value) {
                isDefault.value = true;
            }

            const editMode = () => {
                return props.mode === PAYOUT_METHOD_MODE_EDIT;
            };

            const title = computed(() => {
                return `${editMode() ? 'Edit' : 'Add'} a Payout Method`;
            });

            const primaryButtonText = computed(() => {
                return editMode() ? 'Update' : 'Add Debit Card';
            });

            const secondaryButtonText = computed(() => {
                return editMode() ? 'Remove' : 'Go Back';
            });

            /**
             * Stripe setup
             */
            let stripe, elements, cardNumber, securityCode, expiration;
            if (!editMode()) {
                stripe = Stripe(window.grinEnv.STRIPE_FUNDING_KEY);
                elements = stripe.elements();
                cardNumber = elements?.create('cardNumber', Object.assign({ placeholder: '' }, STRIPE_STYLING));
                securityCode = elements?.create('cardCvc', Object.assign({ placeholder: '' }, STRIPE_STYLING));
                expiration = elements?.create('cardExpiry', Object.assign({ placeholder: '' }, STRIPE_STYLING));
            }

            const cardNumberMasked = () => {
                return '**** **** **** ' + (props.payoutMethod?.last4 || '****');
            };

            const expirationFromString = (expiration) => {
                const split = expiration.split('/');
                return {
                    month: split.length > 0 ? parseInt(split[0]) : 0,
                    year: split.length > 1 ? parseInt(`20${split[1]}`) : 0
                };
            };

            const expirationToString = (month, year) => {
                if (!month || !year) {
                    return '';
                }

                return month.toString().padStart(2, '0') + '/' + year.toString().slice(-2);
            };

            const prefillForm = () => {
                if (!editMode()) {
                    return;
                }

                editCardNumberMasked.value = cardNumberMasked();
                editExpiration.value = expirationToString(
                    props.payoutMethod?.expiration_month,
                    props.payoutMethod?.expiration_year
                );
                postalCode.value = props.payoutMethod?.zip_code;
                cardName.value = props.payoutMethod?.holder_name;
                selectedCountry.value = props.payoutMethod?.country;
                updateCurrencies();
                selectedCurrency.value = props.payoutMethod?.currency;
                isDefault.value = props.payoutMethod?.id === props.payoutAccount?.payout_method_id;
            };

            onMounted(() => {
                if (editMode()) {
                    prefillForm();
                } else {
                    cardNumber?.mount('#card-number');
                    cardNumber?.on('change', (event) => {
                        cardNumberReady.value = event.complete;
                    });

                    securityCode?.mount('#card-security-code');
                    securityCode?.on('change', (event) => {
                        securityCodeReady.value = event.complete;
                    });

                    expiration?.mount('#card-expiration');
                    expiration?.on('change', (event) => {
                        cardExpirationReady.value = event.complete;
                    });
                }
            });

            const { payoutMethod } = toRefs(props);
            watch(payoutMethod, () => {
                prefillForm();
            });

            const debouncedPostalCode = debounce((newVal, oldVal) => {
                if (!newVal || !newVal.toString().match(FORM_POSTAL_CODE_REGEXP)) {
                    postalCode.value = oldVal;
                }
            }, 500);

            watch(postalCode, (newVal, oldVal) => {
                debouncedPostalCode(newVal, oldVal);
            });

            /**
             * Card submission
             */
            const isReady = () => {
                if (!selectedCurrency.value) {
                    return false;
                }

                if (!cardName.value) {
                    return false;
                }

                if (!postalCode.value) {
                    return false;
                }

                if (editMode()) {
                    if (!editCardNumberMasked.value) {
                        return false;
                    }

                    if (!editSecurityCodeMasked.value) {
                        return false;
                    }

                    const exp = expirationFromString(editExpiration.value);
                    const currentYear = new Date().getFullYear();
                    if (!exp.month || exp.year < currentYear) {
                        return false;
                    }
                } else {
                    if (!cardNumberReady.value) {
                        return false;
                    }

                    if (!securityCodeReady.value) {
                        return false;
                    }

                    if (!cardExpirationReady.value) {
                        return false;
                    }
                }

                return true;
            };

            const handleSubmit = () => {
                if (props.mode === PAYOUT_METHOD_MODE_EDIT) {
                    submitUpdateCard();
                } else if (isReady()) {
                    createCard();
                }
            };

            const handleSecondaryAction = () => {
                if (props.mode === PAYOUT_METHOD_MODE_EDIT) {
                    context.emit('remove');
                } else {
                    context.emit('back');
                }
            };

            const { currentValue: isSubmitting, setTrue: submitting, setFalse: doneSubmitting } = useToggle(false);

            const submitUpdateCard = async () => {
                const exp = expirationFromString(editExpiration.value);
                const payload = {
                    holder_name: cardName.value,
                    expiration_month: exp.month,
                    expiration_year: exp.year,
                    billing_address_zip: postalCode.value,
                    is_default: isDefault.value
                };

                submitting();

                const { editVerifiedDebitCard: updateCard } = usePayoutMethods();
                updateCard(contact.id, props.payoutMethod?.id, payload)
                    .then(() => {
                        context.emit('success');
                    })
                    .catch((error) => {
                        message.responseError(error);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            const createCard = async () => {
                const { token, error } = await stripe.createToken(cardNumber, {
                    name: cardName.value,
                    currency: selectedCurrency.value,
                    address_zip: postalCode.value
                });

                if (error) {
                    message.defaultError(error.message);
                    events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_SUBMITTED, getEventPayload(), false);
                }

                submitting();

                const { addVerifiedDebitCard: addCard } = usePayoutMethods();
                addCard(contact.id, token.id, isDefault.value)
                    .then(() => {
                        events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_SUBMITTED, getEventPayload());
                        context.emit('success');
                    })
                    .catch((error) => {
                        message.responseError(error);
                        events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_SUBMITTED, getEventPayload(), false);
                    })
                    .finally(() => {
                        doneSubmitting();
                    });
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    has_additional_payout_method: props.payoutAccount?.external_account?.payout_methods.length > 0
                };
            };

            /**
             * Currency selection
             */
            const handleUpdateCurrencies = () => {
                updateCurrencies();
            };

            const updateCurrencies = () => {
                currencies.value = PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies;
                selectedCurrency.value = PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies?.length === 1 ?
                    PAYOUTS_COUNTRY_CURRENCIES[selectedCountry.value]?.currencies[0].code : undefined;
            };

            const selectedCountry = ref(props.payoutAccount?.country_code);
            const selectedCurrency = ref('');
            const currencies = ref([]);
            updateCurrencies();

            return {
                title,
                primaryButtonText,
                secondaryButtonText,
                handleSecondaryAction,
                editMode,
                editExpiration,
                editCardNumberMasked,
                editSecurityCodeMasked,

                cardName,
                isDefault,
                DEFAULT_PAYOUT_METHOD_LABEL_STRING,
                DEFAULT_PAYOUT_METHOD_LINE_1_STRING,
                DEFAULT_PAYOUT_METHOD_LINE_2_STRING,
                firstPayoutMethod,

                postalCode,
                FORM_POSTAL_CODE_MAX_LENGTH,

                handleSubmit,
                isSubmitting,
                isReady,

                selectedCountry,
                selectedCurrency,
                currencies,
                handleUpdateCurrencies,
                PAYOUTS_COUNTRY_CURRENCIES
            };
        }
    };
</script>

<style lang="scss" scoped>
    .ant-btn.theme-live {
        padding: 0 $g-spacing-10 !important;
    }

    @media only screen and (max-width: $g-screen-size-xs) {
        .ant-row-flex.xs-break .ant-col {
            width: 100%;
        }

        .country-select {
            margin-bottom: $g-spacing-4;
        }
    }
</style>
